import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowUpRightIcon,
  ChevronDownIcon,
  PlusIcon,
  Trash2Icon,
} from "lucide-react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";

import { SectionStatusIndicator } from "./SectionStatusIndicator";
import { Column, KeyboardShortcut, Row, Text } from "@/components";
import { ConfigFormWrapper } from "@/components/companies/shared/ConfigFormWrapper";
import { FormTextInput } from "@/components/form/FormTextInput";
import { VoiceAiQuestionsInfoGuide } from "@/components/form/VoiceAiQuestionsInfoGuide";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleTrigger } from "@/components/ui/collapsible";
import { FormLabel } from "@/components/ui/form";

import { useJobInternalConfigSubmit } from "@/hooks/updateSets/useJobInternalConfigSubmit";
import { JobInternalConfig as JobInternalConfigType } from "@/types";

const voiceAiSchema = z.object({
  voiceAiStaffingDynamicQuestions: z
    .array(
      z.object({
        id: z.number(),
        question: z.string().trim().min(1, "Question cannot be empty"),
      }),
    )
    .optional(),
});

export const VoiceAiSection = ({
  jobInternalConfig,
}: {
  jobInternalConfig: JobInternalConfigType;
}) => {
  /* Database CRUD Operations */
  // Update
  const { handleSubmit, isPending: isSaving } = useJobInternalConfigSubmit(
    jobInternalConfig.id,
    "Voice AI config saved!",
  );

  const form = useForm({
    defaultValues: {
      voiceAiStaffingDynamicQuestions:
        jobInternalConfig.voiceAiStaffingDynamicQuestions ?? [],
    },
    resolver: zodResolver(voiceAiSchema),
  });

  const {
    fields: dynamicQuestions,
    append: appendDynamicQuestion,
    remove: removeDynamicQuestion,
  } = useFieldArray({
    control: form.control,
    name: "voiceAiStaffingDynamicQuestions",
    keyName: "fieldId",
  });

  /* Render */
  const render = () => {
    const questionCount =
      jobInternalConfig.voiceAiStaffingDynamicQuestions?.length ?? 0;

    return (
      <SectionStatusIndicator complete={questionCount >= 3}>
        <Collapsible className="group/collapsible">
          <Column className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <CollapsibleTrigger className="flex w-full cursor-pointer items-center justify-between px-6 py-4">
              <Text variant="header">Voice AI</Text>
              <Row className="items-center gap-2">
                {(() => {
                  const label = `${String(questionCount)} Question${questionCount !== 1 ? "s" : ""}`;
                  if (questionCount === 0)
                    return (
                      <Badge className="rounded-md border-red-200 bg-red-50 text-red-600">
                        {label}
                      </Badge>
                    );
                  if (questionCount < 3)
                    return (
                      <Badge className="rounded-md border-amber-200 bg-amber-50 text-amber-600">
                        {label}
                      </Badge>
                    );
                  return (
                    <Badge className="rounded-md border-emerald-200 bg-emerald-50 text-emerald-600">
                      {label}
                    </Badge>
                  );
                })()}
                <ChevronDownIcon className="h-4 w-4 text-slate-500 transition-transform duration-200 group-data-[state=closed]/collapsible:-rotate-90" />
              </Row>
            </CollapsibleTrigger>
            <ConfigFormWrapper
              buttonLabel="Save Voice AI Config"
              disabled={isSaving}
              form={form}
              onKeyDown={(e) => {
                if (e.shiftKey && e.key === "Enter") {
                  e.preventDefault();
                  appendDynamicQuestion({
                    id: Date.now(),
                    question: "",
                  });
                }
              }}
              onSubmit={handleSubmit}
            >
              <Column className="gap-1">
                <Row className="items-center gap-1">
                  <FormLabel className="text-slate-700">
                    Voice AI Dynamic Questions / Themes
                  </FormLabel>
                  <VoiceAiQuestionsInfoGuide title="Voice AI Dynamic Questions / Themes">
                    <VoiceAiQuestionsInfoGuide.Purpose>
                      These questions or themes will be asked or threaded
                      intelligently during the work experience deep dive portion
                      of the Voice AI call.
                    </VoiceAiQuestionsInfoGuide.Purpose>
                    <VoiceAiQuestionsInfoGuide.Tips>
                      {/* TODO: Add helpful tips content */}
                      <p className="italic text-slate-400">Tips coming soon.</p>
                    </VoiceAiQuestionsInfoGuide.Tips>
                    <VoiceAiQuestionsInfoGuide.Examples>
                      <Column className="gap-2">
                        <Row className="items-start justify-between">
                          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                            <Text variant="small">Company:</Text>
                            <Text variant="muted">WYKO Tire</Text>
                            <Text variant="small">Job Title:</Text>
                            <Text variant="muted">
                              CNC Machinist Apprentice
                            </Text>
                          </div>
                          <a
                            className="inline-flex shrink-0 items-center gap-1 text-xs text-primary-700 hover:underline"
                            href="https://internal-recruiter.laborup.com/companies/6b19bea4-8e8f-4f75-afd6-39c437f8f974/jobs/0dfe6236-046c-4582-89b6-aed3b5ec66ba?t=overview"
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            Link to Job Overview Page
                            <ArrowUpRightIcon className="h-3 w-3" />
                          </a>
                        </Row>
                        <Column className="mt-1 gap-1.5">
                          <Text variant="small">Questions:</Text>
                          <ol className="list-decimal space-y-1.5 pl-5 text-sm text-slate-600">
                            <li>
                              How many years of CNC machining experience do you
                              have? Making what sort of parts?
                            </li>
                            <li>
                              How many years of manual machining do you have?
                              Making what sort of parts?
                            </li>
                            <li>
                              Do you have any formal certifications or technical
                              degrees in machining or machine tool?
                            </li>
                            <li>
                              What is your level of proficiency with blueprint
                              reading and GD&T? Why do you give it that rating?
                            </li>
                            <li>
                              What made you interested in pursuing a long term
                              career in machining? Where would you like to see
                              your machining career in 5 years?
                            </li>
                            <li>
                              If you were to get this apprenticeship role, what
                              would you do to get the most out of it?
                            </li>
                          </ol>
                        </Column>
                        <div className="h-4" />
                        <Column className="gap-2">
                          <Row className="items-start justify-between">
                            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                              <Text variant="small">Company:</Text>
                              <Text variant="muted">Colortech</Text>
                              <Text variant="small">Job Title:</Text>
                              <Text variant="muted">
                                Multi-Craft Maintenance Technician
                              </Text>
                            </div>
                            <a
                              className="inline-flex shrink-0 items-center gap-1 text-xs text-primary-700 hover:underline"
                              href="https://internal-recruiter.laborup.com/companies/0959c097-d7b2-4c66-8c9d-9be0023ea682/jobs/1e7eb295-2cb5-428d-9a11-285430521211?t=overview"
                              rel="noopener noreferrer"
                              target="_blank"
                            >
                              Link to Job Overview Page
                              <ArrowUpRightIcon className="h-3 w-3" />
                            </a>
                          </Row>
                          <Column className="mt-1 gap-1.5">
                            <Text variant="small">Questions:</Text>
                            <ol className="list-decimal space-y-1.5 pl-5 text-sm text-slate-600">
                              <li>
                                How many years of industrial maintenance
                                experience do you have?
                              </li>
                              <li>
                                If you have industrial maintenance experience,
                                in which industries?
                              </li>
                              <li>
                                Across these different industries, what sort of
                                equipment have you been responsible for
                                maintaining?
                              </li>
                              <li>
                                In more detail, can you explain the different
                                types of mechanical maintenance that you&apos;ve
                                performed?
                              </li>
                              <li>
                                How strong is your electrical maintenance
                                experience? What are the different types of
                                electrical maintenance that you&apos;ve
                                performed?
                              </li>
                              <li>
                                If you were to walk up to a motor that
                                isn&apos;t working, can you walk me through your
                                troubleshooting process in detail?
                              </li>
                              <li>
                                Do you have experience troubleshooting with
                                PLCs? Are you able to write or edit programs
                                with PLCs?
                              </li>
                            </ol>
                          </Column>
                        </Column>
                      </Column>
                    </VoiceAiQuestionsInfoGuide.Examples>
                  </VoiceAiQuestionsInfoGuide>
                </Row>
                <Text variant="muted">
                  These questions or themes will be asked or threaded
                  intelligently during the work experience deep dive portion.
                </Text>
              </Column>
              {dynamicQuestions.map((q, index) => (
                <Column key={q.fieldId} className="gap-2">
                  <Row className="items-center justify-between">
                    <FormLabel className="text-sm font-medium text-slate-700">
                      Question / Theme {index + 1}
                    </FormLabel>
                    <Button
                      className="text-slate-400"
                      onClick={() => {
                        removeDynamicQuestion(index);
                      }}
                      size="icon"
                      type="button"
                      variant="ghost"
                    >
                      <Trash2Icon className="h-4 w-4" />
                    </Button>
                  </Row>
                  <FormTextInput
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    fieldName={`voiceAiStaffingDynamicQuestions.${index}.question`}
                    form={form}
                    placeholder="How many years of CNC machining do you have?"
                  />
                </Column>
              ))}
              <Button
                className="w-full border-dashed"
                onClick={() => {
                  appendDynamicQuestion({
                    id: Math.round(Math.random() * Number.MAX_SAFE_INTEGER),
                    question: "",
                  });
                }}
                type="button"
                variant="outline"
              >
                <PlusIcon className="h-4 w-4" />
                Add Question
                <KeyboardShortcut text="⇧↵" />
              </Button>
            </ConfigFormWrapper>
          </Column>
        </Collapsible>
      </SectionStatusIndicator>
    );
  };

  return render();
};
